import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  image?: string;
  structuredData?: Record<string, unknown>;
}

// Función auxiliar para actualizar o crear meta tags
const updateMetaTag = (name: string, content: string, attribute: string = "name") => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
};

// Función auxiliar para actualizar o crear link tags
const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  
  element.setAttribute('href', href);
};

// Función auxiliar para actualizar structured data
const updateStructuredData = (data: Record<string, unknown>) => {
  const scriptId = 'structured-data';
  let script = document.getElementById(scriptId);
  
  if (!script) {
    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  
  script.textContent = JSON.stringify(data);
};

export const useSEO = ({
  title = "SEOgenia - SEO con IA Generativa | Optimización para Agentes IA",
  description = "Especialistas en SEO para IA Generativa: ChatGPT, Perplexity, Gemini. Optimización para agentes IA, schema markup avanzado y arquitectura semántica. Posiciona tu web en el futuro de la búsqueda.",
  keywords = "seo ia generativa, seo chatgpt, seo perplexity, optimización agentes ia, schema markup, arquitectura semántica, seo 2025",
  canonical = "",
  image = "",
  structuredData = {}
}: SEOProps = {}) => {
  const location = useLocation();
  const baseUrl = 'https://seogenia.es';
  const fullUrl = `${baseUrl}${location.pathname}`;
  const defaultImage = `${baseUrl}/og-image.jpg`;
  const finalImage = image || defaultImage;
  const finalCanonical = canonical || fullUrl;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags básicos
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);

    // Canonical
    updateLinkTag("canonical", finalCanonical);

    // Open Graph
    updateMetaTag("og:title", title, "property");
    updateMetaTag("og:description", description, "property");
    updateMetaTag("og:url", finalCanonical, "property");
    updateMetaTag("og:image", finalImage, "property");
    updateMetaTag("og:image:width", "1200", "property");
    updateMetaTag("og:image:height", "630", "property");
    updateMetaTag("og:image:alt", title, "property");
    updateMetaTag("og:type", "website", "property");
    updateMetaTag("og:locale", "es_ES", "property");
    updateMetaTag("og:site_name", "SEOgenia", "property");

    // Twitter Cards
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:site", "@seogenia");
    updateMetaTag("twitter:creator", "@seogenia");
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", finalImage);
    updateMetaTag("twitter:image:alt", title);
    updateMetaTag("twitter:url", finalCanonical);

    // Structured Data
    if (Object.keys(structuredData).length > 0) {
      updateStructuredData(structuredData);
    }
  }, [title, description, keywords, finalCanonical, finalImage, structuredData]);

  return null;
};

export default useSEO;
